import type { RecurrencesMap } from '../../typings'
import type { AnnualCalendarPublicProps } from '@/components/Calendar'

import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { parse, format, isSameDay, lastDayOfYear, startOfYear } from 'date-fns/esm'

import { selectUserConfiguration } from '@/features/domain/user'
import { EPOCH } from '@/server-data'

const now = new Date()

export function useAnnualCalendarProps(
  recurrenceMap: RecurrencesMap,
  onChangeCallback: (date: Date) => void,
): AnnualCalendarPublicProps {
  const { today } = useSelector(selectUserConfiguration)
  const todayDate = parse(today, 'yyyyMMdd', now)

  // Create newToday with baseLeapYear(EPOCH - 1972) but same month and date
  const newTodayDate = new Date(EPOCH.getFullYear(), todayDate.getMonth(), todayDate.getDate())
  const newToday = format(newTodayDate, 'yyyyMMdd')

  const [selection, setSelection] = useState(newTodayDate)

  const onChange = useCallback(
    (newSelection: Date, currentSelection?: Date) => {
      if (currentSelection && isSameDay(currentSelection, newSelection)) {
        return
      }
      onChangeCallback(newSelection)
      setSelection(newSelection)
    },
    [onChangeCallback],
  )

  return useMemo(() => {
    const minDate = format(startOfYear(newTodayDate), 'yyyyMMdd')
    const maxDate = format(lastDayOfYear(newTodayDate), 'yyyyMMdd')

    const daysWithRecurrences =
      !!recurrenceMap && recurrenceMap['ANNUAL']
        ? recurrenceMap['ANNUAL'].reduce((acc, curr) => {
            if (curr.value) {
              const key = format(curr.value, 'yyyyMMdd')
              acc[key] = 1
            }
            return acc
          }, {})
        : undefined

    return {
      today: newToday,
      layout: 'annual',
      selection,
      onChange,
      highlightedDays: daysWithRecurrences,
      minDate,
      maxDate,
    }
  }, [onChange, recurrenceMap, newToday, selection, todayDate])
}
